import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import Brand from './Brand';
import OnboardingTour from './OnboardingTour';
import Hint from './Hint';
import UpgradeModal from './UpgradeModal';
import './Layout.css';

// Module-level cache so the plan banner doesn't refetch /subscription on
// every page navigation (PrivateLayout remounts per route). Short TTL keeps
// the banner near-live after a plan change without a request per navigation.
let planCache = null;
let planCacheAt = 0;
const PLAN_CACHE_MS = 60 * 1000;

const usePlanBanner = () => {
    const [sub, setSub] = useState(planCache);

    useEffect(() => {
        if (planCache && Date.now() - planCacheAt < PLAN_CACHE_MS) return;
        let cancelled = false;
        api.get('subscription')
            .then((data) => {
                planCache = data;
                planCacheAt = Date.now();
                if (!cancelled) setSub(data);
            })
            .catch(() => {}); // banner is decorative — never block the app on it
        return () => {
            cancelled = true;
        };
    }, []);

    if (!sub) return null;

    // Limits are never enforced on admin accounts — don't nag them to upgrade.
    if (sub.adminBypass) return null;

    if (sub.plan === 'free') {
        const left = Number(sub.usage?.searchCredits) || 0;
        return {
            tone: 'upgrade',
            text: left > 0
                ? `You have ${left} free search${left === 1 ? '' : 'es'} left`
                : sub.usage?.hadSearchCredits === false
                    ? 'Your trial has ended'
                    : "You've used your free searches",
            cta: 'Subscribe',
        };
    }

    if (sub.status === 'trialing' && sub.trialEndsAt) {
        const daysLeft = Math.ceil(
            (new Date(sub.trialEndsAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000),
        );
        if (daysLeft <= 14) {
            return {
                tone: 'trial',
                text: `Your Pro trial ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`,
                cta: 'See plans & usage',
            };
        }
    }

    return null;
};

const BASE_TABS = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/traders', label: 'Traders' },
    {
        to: '/new-to-trade',
        label: 'New to trade',
        hint: {
            id: 'new-to-trade',
            title: 'New: fresh leads every month',
            text: 'See the companies that have just started importing or exporting near you. They probably don’t have a forwarder yet.',
        },
    },
    {
        to: '/saved-traders',
        label: 'Saved traders',
        match: '/saved-traders',
        dataOnboard: 'nav-saved',
    },
    { to: '/commodity-codes', label: 'Commodity codes' },
];
const ADMIN_TAB = { to: '/admin/orgs', label: 'Admin', match: '/admin' };

const isActive = (pathname, tab) =>
    pathname.startsWith(tab.match || tab.to);

const AccountMenu = ({ onSettings, onPlan, onLogout }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    return (
        <div className="dash-top-account" ref={ref}>
            <button
                type="button"
                className="dash-account-btn"
                onClick={() => setOpen((v) => !v)}
            >
                <span>Account</span>
                <span className="caret">▾</span>
            </button>
            {open && (
                <div className="dash-account-menu">
                    <button
                        type="button"
                        onClick={() => {
                            setOpen(false);
                            onSettings();
                        }}
                    >
                        Settings
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setOpen(false);
                            onPlan();
                        }}
                    >
                        Plan &amp; usage
                    </button>
                    <hr />
                    <button
                        type="button"
                        onClick={() => {
                            setOpen(false);
                            onLogout();
                        }}
                    >
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
};

const PrivateLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, user } = useAuth();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const banner = usePlanBanner();
    const TABS = user?.is_admin ? [...BASE_TABS, ADMIN_TAB] : BASE_TABS;

    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!drawerOpen) return;
        const onKey = (e) => e.key === 'Escape' && setDrawerOpen(false);
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawerOpen]);

    const onLogout = async () => {
        await logout();
        navigate('/login');
    };

    const tabsList = (
        <nav className="dash-tabs">
            {TABS.map((tab) => {
                const link = (
                    <NavLink
                        key={tab.to}
                        to={tab.to}
                        data-onboard={tab.dataOnboard}
                        className={`dash-tab ${isActive(location.pathname, tab) ? 'on' : ''}`}
                    >
                        {tab.label}
                    </NavLink>
                );
                // A tab can carry a "how to" hint until the user has seen it.
                return tab.hint && !isActive(location.pathname, tab) ? (
                    <Hint key={tab.to} id={tab.hint.id} title={tab.hint.title} text={tab.hint.text}>
                        {link}
                    </Hint>
                ) : link;
            })}
        </nav>
    );

    return (
        <div className="fs dash">
            <header className="dash-top">
                <Brand to="/dashboard" />
                {tabsList}
                <div className="dash-top-spacer" />
                <AccountMenu
                    onSettings={() => navigate('/profile')}
                    onPlan={() => navigate('/subscription')}
                    onLogout={onLogout}
                />
                <button
                    type="button"
                    className="dash-top-menu"
                    aria-label="Open menu"
                    onClick={() => setDrawerOpen(true)}
                >
                    Menu
                </button>
            </header>

            {banner && (
                <div className={`dash-plan-banner ${banner.tone}`}>
                    <span>{banner.text}</span>
                    <button type="button" onClick={() => navigate('/subscription')}>
                        {banner.cta} →
                    </button>
                </div>
            )}

            <div className="dash-body">{children}</div>

            <OnboardingTour />
            <UpgradeModal />

            <div
                className={`dash-drawer ${drawerOpen ? 'open' : ''}`}
                aria-hidden={!drawerOpen}
            >
                <div
                    className="dash-drawer-backdrop"
                    onClick={() => setDrawerOpen(false)}
                />
                <div className="dash-drawer-panel">
                    <div className="dash-drawer-head">
                        <Brand to="/dashboard" />
                        <button
                            type="button"
                            className="dash-drawer-close"
                            aria-label="Close menu"
                            onClick={() => setDrawerOpen(false)}
                        >
                            ✕
                        </button>
                    </div>
                    <div className="dash-drawer-body">
                        <div className="group">
                            <span className="group-label">Navigation</span>
                            {TABS.map((tab) => (
                                <button
                                    key={tab.to}
                                    type="button"
                                    className={`item ${isActive(location.pathname, tab) ? 'on' : ''}`}
                                    onClick={() => {
                                        setDrawerOpen(false);
                                        navigate(tab.to);
                                    }}
                                >
                                    <span>{tab.label}</span>
                                </button>
                            ))}
                        </div>
                        <div className="spacer" />
                        <div className="group bottom">
                            <button
                                type="button"
                                className="item"
                                onClick={() => {
                                    setDrawerOpen(false);
                                    navigate('/profile');
                                }}
                            >
                                <span>Settings</span>
                            </button>
                            <button
                                type="button"
                                className="item"
                                onClick={() => {
                                    setDrawerOpen(false);
                                    navigate('/subscription');
                                }}
                            >
                                <span>Plan &amp; usage</span>
                            </button>
                            <button
                                type="button"
                                className="item"
                                onClick={async () => {
                                    setDrawerOpen(false);
                                    await onLogout();
                                }}
                            >
                                <span>Sign out</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivateLayout;
